import React, { useState } from 'react'
import { Sidebar } from '../../components/Sidebar'
import { Header } from '../../components/Header'
import {
  SearchIcon,
  FilterIcon,
  DownloadIcon,
  MoreVerticalIcon,
  MapPinIcon,
  AwardIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  PlusIcon,
} from 'lucide-react'
import { PARTNERS, Partner } from '../../data/brandMockData'
import { OnboardPartnerModal } from '../../components/brand/OnboardPartnerModal'
export function PartnerManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTier, setSelectedTier] = useState<string>('All')
  const [isOnboardPartnerOpen, setIsOnboardPartnerOpen] = useState(false)
  const filteredPartners = PARTNERS.filter((partner) => {
    const matchesSearch =
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTier = selectedTier === 'All' || partner.tier === selectedTier
    return matchesSearch && matchesTier
  })
  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Platinum':
        return 'bg-purple-100 text-purple-700 border-purple-200'
      case 'Gold':
        return 'bg-amber-100 text-amber-700 border-amber-200'
      case 'Silver':
        return 'bg-gray-100 text-gray-700 border-gray-200'
      case 'Bronze':
        return 'bg-orange-100 text-orange-700 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 75) return 'text-blue-600'
    if (score >= 60) return 'text-amber-600'
    return 'text-red-600'
  }
  return (
    <div className="flex w-full min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Header />
        <div className="px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Partner Management
              </h1>
              <p className="text-gray-500 mt-1">
                Manage dealer network, tiers, and performance
              </p>
            </div>
            <div className="flex gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                <DownloadIcon size={16} />
                Export List
              </button>
              <button
                onClick={() => setIsOnboardPartnerOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 shadow-sm"
              >
                <PlusIcon size={16} />
                Add Partner
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-gray-500 text-sm font-medium mb-1">
                Total Partners
              </div>
              <div className="text-2xl font-bold text-gray-900">42</div>
              <div className="text-green-600 text-xs mt-1 flex items-center">
                <TrendingUpIcon size={12} className="mr-1" /> +3 this month
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-gray-500 text-sm font-medium mb-1">
                Avg Performance
              </div>
              <div className="text-2xl font-bold text-blue-600">84/100</div>
              <div className="text-gray-400 text-xs mt-1">Network average</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-gray-500 text-sm font-medium mb-1">
                Compliance Rate
              </div>
              <div className="text-2xl font-bold text-green-600">92%</div>
              <div className="text-gray-400 text-xs mt-1">Last 30 days</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="text-gray-500 text-sm font-medium mb-1">
                Platinum Partners
              </div>
              <div className="text-2xl font-bold text-purple-600">8</div>
              <div className="text-purple-600 text-xs mt-1">19% of network</div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <SearchIcon
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Search partners by name or location..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <FilterIcon size={18} className="text-gray-400" />
                  <select
                    className="border-none text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer"
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                  >
                    <option value="All">All Tiers</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                    <option value="Bronze">Bronze</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                  Directory
                </button>
                <button className="px-3 py-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                  Scorecards
                </button>
                <button className="px-3 py-1.5 text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                  Onboarding
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-6 py-4 font-semibold">Partner Name</th>
                    <th className="px-6 py-4 font-semibold">Location</th>
                    <th className="px-6 py-4 font-semibold">Tier</th>
                    <th className="px-6 py-4 font-semibold text-center">
                      Performance
                    </th>
                    <th className="px-6 py-4 font-semibold text-center">
                      Compliance
                    </th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPartners.map((partner) => (
                    <tr
                      key={partner.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {partner.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {partner.id}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-gray-600 text-sm">
                          <MapPinIcon
                            size={14}
                            className="mr-1.5 text-gray-400"
                          />
                          {partner.location}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTierColor(partner.tier)}`}
                        >
                          <AwardIcon size={12} className="mr-1" />
                          {partner.tier}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div
                          className={`text-lg font-bold ${getScoreColor(partner.performanceScore)}`}
                        >
                          {partner.performanceScore}
                        </div>
                        <div className="text-xs text-gray-400">Score</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div
                          className={`text-lg font-bold ${getScoreColor(partner.complianceScore)}`}
                        >
                          {partner.complianceScore}%
                        </div>
                        <div className="text-xs text-gray-400">Score</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${partner.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${partner.status === 'Active' ? 'bg-green-500' : 'bg-amber-500'}`}
                          ></span>
                          {partner.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
                          <MoreVerticalIcon size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-sm text-gray-500">
              <div>
                Showing {filteredPartners.length} of {PARTNERS.length} partners
              </div>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 border border-gray-300 rounded bg-white disabled:opacity-50"
                  disabled
                >
                  Previous
                </button>
                <button
                  className="px-3 py-1 border border-gray-300 rounded bg-white disabled:opacity-50"
                  disabled
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <OnboardPartnerModal
        isOpen={isOnboardPartnerOpen}
        onClose={() => setIsOnboardPartnerOpen(false)}
      />
    </div>
  )
}
function PlusIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  )
}
